import React, { useCallback, useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../../../context/auth/auth-context'
import axios from 'axios'
import {
  type AlertColor, Box, Button, Chip, FormControl, FormHelperText, Grid, IconButton, InputLabel, MenuItem, Modal,
  Select, Stack, Typography, useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import GatewayConnectionItem from './gateway-connection-item'
import { formatToLocalMilliTimestampFromUTCNumber } from '../../../../utils/time-utils'
import { formAlertErrorMessage, formAlertResponseMessageWithPrompt, isEmptyString } from '../../../../utils/string-utils'
import ToastWithMessage from '../../../../components/toast/toast-with-message'
import ViewJSONTrigger from '../view/view-json-trigger'

interface GatewayModalProps {
  open: boolean
  onClose: () => void
  strategyId: string
  title: string
  dataUrl: string | null
  postUrl: string | null
}

interface GatewayInfo {
  gatewayConnections: Record<string, [{ id: number, uri: string, params: string }]>
  gatewayStates: Record<string, { name: string, states: Record<string, any>, parameters: Record<string, any> }>
  symbols: string[]
  accounts: string[]
}

export interface ConnectionInfo {
  identifier: string
  name: string
  connections: [{
    id: number
    uri: string
    params: string
  }]
  states: Record<string, any>
  parameters: Record<string, any>
}

const GatewayModal: React.FC<GatewayModalProps> = (
  { open, onClose, strategyId, title, dataUrl, postUrl }
) => {
  const theme = useTheme()
  const [data, setData] = useState<Record<string, ConnectionInfo>>({})
  const [symbols, setSymbols] = useState<string[]>([])
  const [accounts, setAccounts] = useState<string[]>([])
  const [retrievalTimestamp, setRetrievalTimestamp] = useState<number>(0)

  const [selectedSymbol, setSelectedSymbol] = useState<string>('')
  const [selectedStream, setSelectedStream] = useState<'depth' | 'trade'>('depth')
  const [selectedStreamType, setSelectedStreamType] = useState<'-' | 'raw' | 'aggregate'>('-')
  const [selectedAccount, setSelectedAccount] = useState<string>('')

  const { isAuthorized, accessToken } = useContext(AuthContext)

  const getGatewayInfo = useCallback(() => {
    if (dataUrl != null && dataUrl.length !== 0) {
      axios
        .get(dataUrl, { headers: { Authorization: `Bearer ${accessToken}` } })
        .then(response => {
          const data: GatewayInfo = response.data

          const connectionInfos: Record<string, ConnectionInfo> = {}
          const gatewayConnections = data.gatewayConnections
          const gatewayStates = data.gatewayStates

          for (const [identifier, value] of Object.entries(gatewayStates)) {
            connectionInfos[identifier] = {
              identifier,
              name: value.name,
              connections: gatewayConnections[identifier] ?? [],
              states: value.states,
              parameters: value.parameters,
            }
          }

          setSymbols(data.symbols ?? [])
          setAccounts(data.accounts ?? [])
          setData(connectionInfos)
          setRetrievalTimestamp(Date.now())
        })
        .catch(reason => {
          console.error(reason)
          setSymbols([])
          setAccounts([])
          setData({})
          setRetrievalTimestamp(-1)
        })
    }
  }, [isAuthorized, accessToken, dataUrl])

  useEffect(() => {
    if (open && isAuthorized) {
      setSelectedSymbol('')
      setSelectedStream('depth')
      setSelectedStreamType('-')
      setSelectedAccount('')
      getGatewayInfo()
    }
  }, [open, isAuthorized])

  const postRequest = useCallback((identifier: string, message: object) => {
    if (postUrl == null || postUrl.length === 0) {
      console.debug('[Gateway Modal] Post url is empty')
      displayToastWithMessage('The POST url is invalid - please check the related configuration again', 'warning')
      return
    }

    console.debug(`[Gateway Modal] Queuing update request for: ${identifier}`)
    console.debug(message)

    axios
      .post(postUrl, message, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(response => {
        console.debug(response)
        displayToastWithMessage(formAlertResponseMessageWithPrompt(response), 'success')
      })
      .catch(error => {
        console.error(error)
        displayToastWithMessage(formAlertErrorMessage(error), 'error')
      })
      .finally(() => {
        getGatewayInfo()
      })
  }, [strategyId, postUrl, accessToken])

  const handleManualReconnectSymbol = useCallback(() => {
    if (isEmptyString(selectedSymbol) || isEmptyString(selectedStream)) {
      console.debug('[Gateway Modal] Symbol and stream cannot be empty')
      displayToastWithMessage('Symbol and stream cannot be empty', 'warning')
      return
    }
    if (selectedStream === 'trade' && (selectedStreamType === '-' || isEmptyString(selectedStreamType))) {
      console.debug('[Gateway Modal] Stream type cannot be empty')
      displayToastWithMessage('Stream type cannot be empty', 'warning')
      return
    }

    let identifier
    let message
    if (selectedStream === 'depth') {
      identifier = `${selectedSymbol},${selectedStream}`
      message = {
        timestamp: new Date().toISOString(),
        strategyId,
        command: `${selectedSymbol},${selectedStream},reconnect`,
      }
    } else {
      identifier = `${selectedSymbol},${selectedStream},${selectedStreamType}`
      message = {
        timestamp: new Date().toISOString(),
        strategyId,
        command: `${selectedSymbol},${selectedStream},${selectedStreamType},reconnect`,
      }
    }

    postRequest(identifier, message)
  }, [postRequest, postUrl, strategyId, selectedSymbol, selectedStream, selectedStreamType])

  const handleManualReconnectAccount = useCallback(() => {
    if (isEmptyString(selectedAccount)) {
      console.debug('[Gateway Modal] Account cannot be empty')
      displayToastWithMessage('Account cannot be empty', 'warning')
      return
    }

    const message = {
      timestamp: new Date().toISOString(),
      strategyId,
      command: `${selectedAccount},order_fill,reconnect`,
    }

    postRequest(selectedAccount, message)
  }, [postRequest, postUrl, strategyId, selectedAccount])

  const handleReconnect = useCallback((identifier: string, symbol: string | null) => {
    const identifierLowercase = identifier.toLowerCase()
    let message

    if (identifierLowercase.startsWith('depth')) {
      message = {
        timestamp: new Date().toISOString(),
        strategyId,
        command: `${symbol},depth,reconnect`,
      }
    }

    if (message == null) {
      console.debug(`[Gateway Modal] Unsupported operation for the stream: ${identifier} with symbol: ${symbol}`)
      displayToastWithMessage('Unsupported operation - use manual reconnect', 'warning')
      return
    }

    postRequest(`${identifier},${symbol}`, message)
  }, [postRequest, postUrl, strategyId])

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState<AlertColor>('info')
  const displayToastWithMessage = useCallback((message: string, severity: AlertColor): void => {
    setToastMessage(message)
    setToastSeverity(severity)
    setToastOpen(true)
  }, [strategyId])

  const onModalClose = useCallback(() => {
    setToastOpen(false)
    onClose()
  }, [onClose])

  return (
    <Modal
      open={open}
      onClose={onModalClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Stack
        sx={{
          position: 'absolute' as 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '1600px',
          height: '80%',
          maxHeight: '1000px',
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.primary.main}`,
          boxShadow: 24,
          p: 4,
          color: 'white',
          overflowY: 'auto',
        }}
        gap={2}
      >
        <Stack direction='row' alignItems='center' gap={1}>
          <Typography id='modal-modal-title' fontWeight='bold' variant='h5' component='h1'>
            {title}
          </Typography>
          <Chip
            label={formatToLocalMilliTimestampFromUTCNumber(retrievalTimestamp)}
            size='small'
          />
          <ViewJSONTrigger dataUrl={dataUrl}/>
          <Box marginX='auto'></Box>
          <IconButton color='error' onClick={onModalClose}>
            <CloseIcon/>
          </IconButton>
        </Stack>

        <Button variant='outlined' sx={{ width: 'fit-content' }} startIcon={<RefreshIcon/>} onClick={getGatewayInfo}>
          Reload
        </Button>

        <Typography fontWeight='bold' variant='h6' component='h2'>Manual Reconnect</Typography>

        <Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={3}>
              <Stack gap={1}>
                <Typography color='primary' fontSize='large' fontWeight='bold'>Stream</Typography>

                <FormControl variant='standard' fullWidth error={Object.keys(symbols).length > 0 && isEmptyString(selectedSymbol)}>
                  <InputLabel id='symbol-select-label'>{Object.keys(symbols).length === 0 ? 'No Symbols Found' : 'Symbol'}</InputLabel>
                  <Select
                    labelId='symbol-select-label'
                    id='symbol-select'
                    value={selectedSymbol}
                    onChange={event => {
                      setSelectedSymbol(event.target.value)
                    }}
                    disabled={Object.keys(symbols).length === 0}
                  >
                    {
                      symbols
                        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                        .map(symbol => {
                          return <MenuItem key={symbol} value={symbol}>{symbol}</MenuItem>
                        })
                    }
                  </Select>
                  {
                    Object.keys(symbols).length > 0 && isEmptyString(selectedSymbol) &&
                    <FormHelperText>Select a symbol</FormHelperText>
                  }
                </FormControl>

                <FormControl variant='standard' fullWidth>
                  <InputLabel id='stream-select-label'>Stream</InputLabel>
                  <Select
                    labelId='stream-select-label'
                    id='stream-select'
                    label='Stream'
                    value={selectedStream}
                    onChange={event => {
                      // @ts-expect-error options are limited
                      setSelectedStream(event.target.value)
                      if (event.target.value === 'depth') {
                        setSelectedStreamType('-')
                      }
                    }}
                  >
                    <MenuItem key='depth' value='depth'>Depth</MenuItem>
                    <MenuItem key='trade' value='trade'>Trade</MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  variant='standard'
                  fullWidth
                  error={Object.keys(symbols).length > 0 && (selectedStream === 'trade' && (selectedStreamType === '-' || isEmptyString(selectedStreamType)))}>
                  <InputLabel id='stream-type-select-label'>Stream Type</InputLabel>
                  <Select
                    labelId='stream-type-select-label'
                    id='stream-type-select'
                    label='Stream Type'
                    value={selectedStreamType}
                    onChange={event => {
                      // @ts-expect-error options are limited
                      setSelectedStreamType(event.target.value)
                    }}
                    disabled={selectedStream === 'depth'}
                  >
                    <MenuItem key='-' value='-'>-</MenuItem>
                    <MenuItem key='raw' value='raw'>Raw</MenuItem>
                    <MenuItem key='aggregate' value='aggregate'>Aggregate</MenuItem>
                  </Select>
                  {
                    Object.keys(symbols).length > 0 && (selectedStream === 'trade' && (selectedStreamType === '-' || isEmptyString(selectedStreamType))) &&
                    <FormHelperText>Select a stream type</FormHelperText>
                  }
                </FormControl>

                <Button
                  variant='outlined'
                  startIcon={<RefreshIcon/>}
                  onClick={handleManualReconnectSymbol}
                  disabled={
                    isEmptyString(selectedSymbol) || isEmptyString(selectedStream) ||
                    (selectedStream === 'trade' && (selectedStreamType === '-' || isEmptyString(selectedStreamType)))
                  }
                >
                  Reconnect Symbol
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <Stack gap={1}>
                <Typography color='primary' fontSize='large' fontWeight='bold'>Order Listener</Typography>

                <FormControl variant='standard' fullWidth error={Object.keys(accounts).length > 0 && isEmptyString(selectedAccount)}>
                  <InputLabel id='account-select-label'>{Object.keys(accounts).length === 0 ? 'No Accounts Found' : 'Account'}</InputLabel>
                  <Select
                    labelId='account-select-label'
                    id='account-select'
                    value={selectedAccount}
                    onChange={event => {
                      setSelectedAccount(event.target.value)
                    }}
                    disabled={Object.keys(accounts).length === 0}
                  >
                    {
                      accounts
                        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                        .map(account => {
                          return <MenuItem key={account} value={account}>{account}</MenuItem>
                        })
                    }
                  </Select>
                  {
                    Object.keys(accounts).length > 0 && isEmptyString(selectedAccount) &&
                    <FormHelperText>Select an account</FormHelperText>
                  }
                </FormControl>

                <Button
                  variant='outlined'
                  startIcon={<RefreshIcon/>}
                  onClick={handleManualReconnectAccount}
                  disabled={isEmptyString(selectedAccount)}
                >
                  Reconnect Account
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Stack>

        <Typography fontWeight='bold' variant='h6' component='h2'>All Connections</Typography>

        <Grid container spacing={2}>
          {
            Object.entries(data)
              .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
              .map(([identifier, connectionInfo]) =>
                <GatewayConnectionItem
                  key={identifier}
                  connectionInfo={connectionInfo}
                  reconnectCallback={handleReconnect}
                />,
              )
          }
        </Grid>

        <ToastWithMessage toastOpen={toastOpen} setToastOpen={setToastOpen} message={toastMessage} severity={toastSeverity}/>
      </Stack>
    </Modal>
  )
}

export default GatewayModal
